import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { string, func, bool } from 'prop-types';
import cn from 'classnames';

import CheckIcon from 'src/assets/icons/check.svg';
import { LOCAL_UPLOAD } from 'src/constants/uploadFile';
import { VIDEO_ELEMENT, IMAGE_ELEMENT } from 'src/constants/canvasElements';
import { snapImageOfVideo, isVideo } from 'src/utils/videoHelpers';
import { isGif } from 'src/utils/helpers';
import { optimizeCloudinaryThumbnail } from 'src/utils/cloudinaryHelpers';
import styles from './UploadedMediaCard.module.scss';

const UploadedMediaCard = ({
  preview,
  addImage,
  isSelected,
  id,
  name,
  showName = true,
  disabled,
  className,
  imageClassName,
  imageButtonClassName,
  optimizeImage = true,
}) => {
  const asset = useMemo(() => ({
    src: preview,
    id,
    name,
    uploadType: LOCAL_UPLOAD,
  }), [id, name, preview]);

  if (isVideo(preview)) {
    asset.type = VIDEO_ELEMENT;
  } else {
    asset.type = IMAGE_ELEMENT;
  }

  const onClick = useCallback(() => {
    addImage(asset);
  }, [addImage, asset]);

  const [previewSrc, setPreviewSrc] = useState();

  useEffect(() => {
    if (isVideo(preview)) {
      snapImageOfVideo(preview, (videoThumbSrc) => {
        setPreviewSrc(videoThumbSrc);
      });
    } else if (isGif(preview)) {
      setPreviewSrc(preview);
    } else if (optimizeImage) {
      const imageThumbnail = optimizeCloudinaryThumbnail(preview);
      setPreviewSrc(imageThumbnail);
    } else {
      setPreviewSrc(preview);
    }
  }, [preview, asset, optimizeImage]);

  return (
    <div className={cn(styles.container, className)}>
      <button
        className={cn(styles.imageContainer, imageButtonClassName)}
        onClick={onClick}
        disabled={disabled}
      >
        <img src={previewSrc} alt="" className={cn(styles.image, imageClassName)} />
        {isSelected && (
          <div className={styles.overlay}>
            <div className={styles.check}>
              <img src={CheckIcon} alt="Check" />
            </div>
          </div>
        )}
      </button>
      {showName && (
        <span className={styles.name}>
          {name}
        </span>
      )}
    </div>
  );
};

UploadedMediaCard.propTypes = {
  preview: string.isRequired,
  addImage: func.isRequired,
  isSelected: bool.isRequired,
  id: string.isRequired,
  name: string,
  disabled: bool,
  className: string,
  imageClassName: string,
  imageButtonClassName: string,
  showName: bool,
  optimizeImage: bool,
};

export { UploadedMediaCard };
